import React, { useState, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { message, Spin, Tooltip, Checkbox, Typography } from 'antd';
import {
  fetchStudentListData,
  setSelectedStudents,
  setSelectAll,
  setCheckBoxState,
  postLogOfflineAttendance,
  setSelectedId
} from 'actions/schedule';
import { attendanceRequirementDetail } from 'actions/attendanceNew';
import { Api, Notification } from 'services';
import { SearchOutlined, ControlOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import MagnifierIcon from '../../Assets/schedule/magnifier.svg';
import FilterIcon from '../../Assets/schedule/filter.svg';
import emptyGhost from '../../Assets/schedule/Group 10647.svg';
import { canMarkAttendanceFor } from './schedulePermissions';

// Helper to normalize batch names for consistent comparison
const normalizeBatchName = (name) => {
  if (!name || name === 'N/A') return 'N/A';
  // Standardize: replace hyphens/colons with ' : ', collapse whitespace, trim
  return String(name)
    .replace(/[-:]/g, ' : ')
    .replace(/\s+/g, ' ')
    .replace(/\s*:\s*/g, ' : ')
    .trim();
};

// Helper to extract EVERY potential batch identifier from a student object
const getBatchFacets = (student, schedule) => {
  if (!student) return new Set();
  const scheduleStandards = schedule?.standards || [];
  const facets = new Set();
  const ids = new Set();

  const addName = (n) => { if (n && n !== 'N/A') facets.add(normalizeBatchName(n)); };
  const addId = (id) => { if (id) ids.add(String(id)); };

  // 1. Check all known naming fields (Direct & Nested)
  const targets = [student, student.student].filter(Boolean);
  targets.forEach(t => {
    addName(t.batchName);
    addName(t.batch);
    if (t.std) addName(t.section ? `${t.std} : ${t.section}` : t.std);
    addName(t.standard_name);
    addName(t.standardName);

    addId(t.standardId);
    addId(t.standard_id);
    addId(t.batchId);
    addId(t.batch_id);
    addId(t.classId);
    addId(t.class_id);
    if (t.id && t.id !== student.studentId) addId(t.id); // Some APIs use .id for standardId in nested objects

    // Check standards arrays
    const stds = t.standards || t.studentStandards || t.standard_details || [];
    if (Array.isArray(stds)) {
      stds.forEach(s => {
        if (typeof s === 'object' && s !== null) {
          addId(s.id);
          addId(s.standardId);
          addId(s.standard_id);
          const nested = s.standard || s;
          if (nested.std) addName(nested.section ? `${nested.std} : ${nested.section}` : nested.std);
          else addName(nested.name);
        } else {
          addId(s);
        }
      });
    }
  });

  // 2. Fragmented fields like "code"
  if (student.code && student.code.includes('|')) {
    addName(student.code.split('|')[1]);
  }

  // 3. Map all found IDs to Names from the schedule
  ids.forEach(id => {
    const match = scheduleStandards.find(ss => String(ss.id) === id);
    if (match) {
      if (match.std) addName(match.section ? `${match.std} : ${match.section}` : match.std);
      else addName(match.name);
    }
  });

  // 4. Fallback: If no facets found, user belongs to the schedule, so attach schedule's standards
  if (facets.size === 0 && Array.isArray(scheduleStandards)) {
    scheduleStandards.forEach(match => {
      if (match.std) addName(match.section ? `${match.std} : ${match.section}` : match.std);
      else addName(match.name);
    });
  }

  return facets;
};

// Helper to get a singular display string for the student card
const getBatchName = (student, schedule) => {
  const facets = getBatchFacets(student, schedule);
  if (facets.size > 0) return Array.from(facets)[0];
  return 'N/A';
};

const WhiteCheck = () => (
  <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 5.5L4.5 9L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* Attendance status badges: a 22px disc with a 1px inset white border. The check
   is 8.8 x 6.6 and the cross 7 x 7, both 2px white stroke, per the design spec. */
const PresentBadge = () => (
  <span className="status-badge status-badge-present" role="img" aria-label="Present">
    <svg width="9" height="7" viewBox="0 0 9 7" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 3.4L3.2 5.6L7.8 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const AbsentBadge = () => (
  <span className="status-badge status-badge-absent" role="img" aria-label="Absent">
    <svg width="7" height="7" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 1L6 6M6 1L1 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0][0].toUpperCase();
};

const getAvatarBg = (name) => {
  const colors = ['#E8E2FF', '#DFF6DD', '#FFF4D1', '#FFEDED', '#E2F7FF'];
  if (!name) return colors[0];
  const charCode = name.charCodeAt(0);
  return colors[charCode % colors.length];
};

const getAvatarColor = (bg) => {
  const mapping = {
    '#E8E2FF': '#594099',
    '#DFF6DD': '#2D8A29',
    '#FFF4D1': '#B08800',
    '#FFEDED': '#C23939',
    '#E2F7FF': '#0078A8'
  };
  return mapping[bg] || '#594099';
};

const isDefaultImage = (img) => {
  if (!img) return true;
  const defaults = ['test.png', 'user.png', 'default.png', 'student.png', 'placeholder.png'];
  return defaults.some(d => img.toLowerCase().includes(d));
};;

// Minutes attended, as reported by /attendancev2/student-list (falls back to the
// older field names the same endpoint used to send).
const getDurationMins = (student) => {
  if (!student) return 0;
  const raw =
    student.duration ??
    student.totalDuration ??
    student.attendanceDuration ??
    student.student?.duration ??
    student.student?.totalDuration ??
    student.student?.attendanceDuration;
  const mins = parseInt(raw, 10);
  return Number.isFinite(mins) ? mins : 0;
};

// An Others class is held wherever its link points, somewhere this app never sees,
// so the duration it reports was not measured here and never decides attendance:
// the roster reads absent until a teacher marks it by hand. Both spellings appear
// in schedule data.
const isOthersMode = (student, schedule) => {
  const sMode = String(schedule?.mode || schedule?.scheduleMode || '').toLowerCase();
  if (sMode === 'others' || sMode === 'other') return true;
  const mode = String(student?.mode || student?.scheduleMode || '').toLowerCase();
  return mode === 'others' || mode === 'other';
};

// A present/absent call the server actually holds, as opposed to one the duration
// implies. For an Others class this is the only thing that can make a student
// present - and it is what a teacher's own marking comes back as, so marks stay
// put across the refetch that follows an update.
const isExplicitlyPresent = (student) =>
  student?.isPresent === true ||
  String(student?.status).toLowerCase() === 'present' ||
  student?.student?.isPresent === true ||
  String(student?.student?.status).toLowerCase() === 'present';

// Whether the class has any attendance record for this student at all. A student who
// joined and left straight away reports 0 minutes, which is a different thing from one
// who never joined: the first gets an auto-marked status, the second a manual checkbox.
// UPDATE: Per user request, 0 mins or empty means manual attendance marking (square checkbox).
const hasAttendanceRecord = (student, schedule) => {
  if (!student) return false;

  const raw =
    student.duration ??
    student.totalDuration ??
    student.attendanceDuration ??
    student.student?.duration ??
    student.student?.totalDuration ??
    student.student?.attendanceDuration;

  if (raw !== undefined && raw !== null && raw !== '') {
    const num = parseInt(raw, 10);
    if (num > 0) return true;
  }
  // If duration is 0 or undefined, they are manual, unless they specifically have a joinTime in broadcast/online
  if (!isOthersMode(student, schedule) && (student.joinTime || student.jointime || student.joinedAt)) {
    const num = parseInt(raw, 10);
    if (num > 0) return true;
  }
  return false;
};

// How the class decided this student's attendance, given the minutes cut-off for the
// class (a percentage of its length, see getAttendanceThresholdMins):
//   'manual' - never joined, so a teacher has to mark them by hand
//   'auto'   - joined, so the duration against the requirement decides it
const getAttendanceSource = (student, schedule) => hasAttendanceRecord(student, schedule) ? 'auto' : 'manual';

// Whether an auto-marked student met the requirement. The API's own call wins; the
// duration is only consulted when it has not made one.
const meetsAttendanceRequirement = (student, thresholdMins) => {
  const isExplicitPresent =
    student?.isPresent === true ||
    student?.student?.isPresent === true ||
    String(student?.status || student?.student?.status || '').toLowerCase() === 'present';
  if (isExplicitPresent) return true;

  const isExplicitAbsent =
    student?.isPresent === false ||
    student?.student?.isPresent === false ||
    String(student?.status || student?.student?.status || '').toLowerCase() === 'absent';
  if (isExplicitAbsent) return false;

  if (!Number.isFinite(thresholdMins)) return false;
  return getDurationMins(student) >= thresholdMins;
};

function StudentListComponent({
  schedule,
  // Redux State
  studentAttendanceData,
  selectedStudents,
  studentListLoading,
  initialSummary,
  attendanceThresholdMins,
  minAttendancePercentageStudent,
  // Redux Actions
  fetchStudentListData,
  attendanceRequirementDetail,
  setSelectedStudents,
  postLogOfflineAttendance,
  setSelectedId,
  togglePageLoader,
  activeTab,
  isScheduledClass,
  isCompletedClass,
  isOngoingClass,
  isCancelledClass,
  user
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isSearchHovered, setIsSearchHovered] = useState(false);
  const [appliedModeFilter, setAppliedModeFilter] = useState('All');
  const [appliedBatchFilter, setAppliedBatchFilter] = useState({ All: ['All'], Online: ['All'], Offline: ['All'] });
  const [hasModified, setHasModified] = useState(false);
  const [updating, setUpdating] = useState(false);
  
  // Who may mark this class's attendance comes straight from the role x privilege matrix
  // (schedulePermissions.js). The three outcomes it can produce:
  //   ALL         - SuperAdmin/Admin/Planner/AttendanceMarker: any class, any state.
  //   OWN_ONGOING - a Teacher holding no privilege: their own class, and only while it
  //                 is actually running.
  //   NONE        - e.g. a Data Operator with no privilege; the tab is hidden upstream.
  // Restore the previous privilege-list version with:
  const canEditAttendance = canMarkAttendanceFor(user, schedule, isOngoingClass) && !isCancelledClass && !isScheduledClass;
  
  // Temporary states for the dropdown UI
  const [tempModeFilter, setTempModeFilter] = useState('All');
  const [tempBatchFilter, setTempBatchFilter] = useState({ All: ['All'], Online: ['All'], Offline: ['All'] });
  
  const filterRef = useRef(null);

  // Sync temp filters with applied filters when dropdown opens
  // The present/absent cut-off comes from /attendance-requirements, which only the
  // attendance pages load. Pull it in so the drawer works on a cold start too.
  useEffect(() => {
    if (activeTab === 'attendance' && !minAttendancePercentageStudent) {
      attendanceRequirementDetail();
    }
  }, [activeTab, minAttendancePercentageStudent]);

  useEffect(() => {
    if (showFilters) {
      setTempModeFilter(appliedModeFilter);
      setTempBatchFilter(appliedBatchFilter);
    }
  }, [showFilters]);

  // Show snackbar if cancelled or scheduled class
  useEffect(() => {
    if (activeTab === 'attendance') {
      // Nothing has gone wrong here - the class simply isn't one attendance applies
      // to - so these are info, not errors, and must not arrive under a red cross.
      if (isCancelledClass) {
        Notification.info('Info', 'Attendance cannot be marked for cancelled class');
      } else if (isScheduledClass) {
        Notification.info('Info', 'Attendance cannot be marked for scheduled class');
      }
    }
  }, [activeTab, isCancelledClass, isScheduledClass]);

  // Robust ID extraction for unique occurrences (prevents attendance leakage between same-subject/same-batch classes at different times)
  // We check nested occurance/occurrence objects as well as top-level fields
  const occurrenceId = schedule?.occurrenceId || schedule?.occuranceId || schedule?.occurrence_id || 
                       schedule?.occurance?.id || schedule?.occurrence?.id || 
                       schedule?.id || schedule?.scheduleId;

  // Reset state and search on tab change to clear unsaved changes
  useEffect(() => {
    setSearchQuery('');
    setAppliedModeFilter('All');
    setAppliedBatchFilter({ All: ['All'], Online: ['All'], Offline: ['All'] });
    setShowFilters(false);
    
    if (occurrenceId) {
      fetchStudentListData(occurrenceId);
      setHasModified(false);
    }
  }, [activeTab, occurrenceId, fetchStudentListData]);

  useEffect(() => {
    if (occurrenceId) {
      setSelectedId(occurrenceId);
    }
  }, [occurrenceId]);

  // Explain a read-only roster whenever it is read-only, not only on completed classes.
  // The two reasons are different and a single message for both would be misleading: a
  // bare Teacher CAN mark their own class, but only while it is running.
  useEffect(() => {
    if (activeTab !== 'attendance' || canEditAttendance || isCancelledClass || isScheduledClass) return;
    const isOwnClassTeacher =
      user?.role?.includes('Teacher') &&
      (String(user?.id) === String(schedule?.teacher?.id) || String(user?.id) === String(schedule?.teacherId));
    // Was Notification.success, so a refusal arrived under a green tick; and the copy
    // named only admins when planners, attendance markers and the class's own teacher
    // can mark too. Restore for the previous toast:
    // Notification.success("Attendance", "Only admin and super admin can mark the attendance");
    Notification.warning(
      "Attendance",
      isOwnClassTeacher
        ? "You can mark attendance for your class only while it is ongoing"
        : "You don't have permission to mark attendance for this class"
    );
  }, [activeTab, canEditAttendance, user, schedule]);

  // Close filter popout when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Extract unique batches for filtering
  const batches = useMemo(() => {
    const uniqueBatches = new Set();

    // Always include ALL batches from schedule standards first
    if (schedule?.standards) {
      schedule.standards.forEach(b => {
        if (b.std && b.section) uniqueBatches.add(normalizeBatchName(`${b.std} : ${b.section}`));
        else if (b.std) uniqueBatches.add(normalizeBatchName(b.std));
        else if (b.name) uniqueBatches.add(normalizeBatchName(b.name));
      });
    }

    // Also include any batches found in student data (just in case)
    const students = studentAttendanceData || [];
    students.forEach((student) => {
      const bName = getBatchName(student, schedule);
      if (bName && bName !== 'N/A') uniqueBatches.add(bName);
    });

    return Array.from(uniqueBatches).sort();
  }, [studentAttendanceData, schedule]);

  // What the server holds for a student right now, which is both what a row starts
  // out showing and what "has this been changed?" is measured against.
  const serverPresence = (student) => (
    getAttendanceSource(student, schedule) === 'auto'
      ? meetsAttendanceRequirement(student, attendanceThresholdMins)
      : isOthersMode(student, schedule)
        ? isExplicitlyPresent(student)
        : false
  );

  // Check if any student's current state (selectedStudents) differs from their initial server state
  const isDirty = useMemo(() => {
    if (!studentAttendanceData || studentAttendanceData.length === 0) return false;

    return studentAttendanceData.some(s => {
      const currentSelected = !!selectedStudents[s.studentId];
      const initialPresent = serverPresence(s);
      return currentSelected !== initialPresent;
    });
  }, [studentAttendanceData, selectedStudents, attendanceThresholdMins]);

  // Filtering logic
  const filteredStudents = useMemo(() => {
    let list = studentAttendanceData || [];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      list = list.filter(s =>
        (s.name && s.name.toLowerCase().includes(query)) ||
        (s.rollNo && String(s.rollNo).toLowerCase().includes(query)) ||
        (s.studentId && String(s.studentId).toLowerCase().includes(query))
      );
    }

    // Mode filter
    if (appliedModeFilter !== 'All') {
      list = list.filter(s => {
        const studentMode = (s.mode || '').toLowerCase();
        const durationNum = getDurationMins(s);

        if (appliedModeFilter === 'Online') {
          // Online means Broadcast. Include if mode is 'broadcast'/'online' OR they have duration.
          return studentMode === 'online' || studentMode === 'broadcast' || durationNum > 0;
        } 
        if (appliedModeFilter === 'Offline') {
          // Offline means they haven't joined the broadcast session.
          return studentMode === 'offline' || (!s.mode && durationNum === 0);
        }
        return true;
      });
    }

    // Batch filter
    const activeAppliedBatches = appliedBatchFilter[appliedModeFilter];
    if (!activeAppliedBatches.includes('All')) {
      const normalizedSelected = activeAppliedBatches.map(f => normalizeBatchName(f));
      list = list.filter(s => {
        const potentialNames = getBatchFacets(s, schedule);

        // If the student has NO recognizable batch but they are in this list, 
        // and there is only ONE batch in the schedule, we can optionally 
        // attribute them to it. But to be safe, we check all facets.

        // Check if ANY of the student's batch facets match ANY of the selected filters
        const facets = Array.from(potentialNames);
        return facets.some(name => normalizedSelected.includes(name));
      });
    }

    return list;
  }, [studentAttendanceData, searchQuery, appliedModeFilter, appliedBatchFilter, schedule]);

  // Categorization Logic based on design images
  const { presentStudents, absentStudents, isBatchMarked } = useMemo(() => {
    const present = [];
    const absent = [];
    let someoneMarkedFromApi = false;

    // An Others class has no present count worth trusting - the server works it out
    // from a duration this app never measured - so only an explicit mark can open
    // the split view there, and an unmarked roster stays one plain list.
    const scheduleIsOthers = isOthersMode(null, schedule);

    // First pass: Determine if ANY students have an explicit status from the API or attendance records
    filteredStudents.forEach(s => {
      const statusStr = String(s.status || s.student?.status || '').toLowerCase();
      const isApiMarked =
        s.isPresent === true || s.isPresent === false ||
        s.student?.isPresent === true || s.student?.isPresent === false ||
        statusStr === 'present' || statusStr === 'absent' ||
        getAttendanceSource(s, schedule) === 'auto';
      if (isApiMarked) someoneMarkedFromApi = true;
    });

    // Check if attendance was already marked on the server.
    // We show the split view ONLY IF someone is already present.
    // "All students should show initially when no one is marked as present or absent"
    const wasAlreadyMarked = scheduleIsOthers
      ? (someoneMarkedFromApi || initialSummary?.presentCount > 0)
      : (initialSummary?.presentCount > 0 || someoneMarkedFromApi);

    // Second pass: Partition students based ONLY on API presence/duration, NOT on local selection state.
    // This ensures students stay in their sections while you are marking them, and only move after an update.
    filteredStudents.forEach(s => {
      const isApiPresent = getAttendanceSource(s, schedule) === 'auto'
        ? meetsAttendanceRequirement(s, attendanceThresholdMins)
        : isOthersMode(s, schedule)
          ? isExplicitlyPresent(s)
          : false;

      if (isApiPresent) {
        present.push(s);
      } else {
        absent.push(s);
      }
    });

    // Show split view ONLY IF it was already marked on the server
    const showSplitView = wasAlreadyMarked;

    return { 
      presentStudents: present, 
      absentStudents: absent, 
      isBatchMarked: showSplitView 
    };
  }, [filteredStudents, selectedStudents, initialSummary, attendanceThresholdMins]);

  const handleUpdate = () => {
    if (!occurrenceId) return;
    setUpdating(true);
    postLogOfflineAttendance(() => {
      setUpdating(false);
      
      // Clear filters and search after update
      setSearchQuery('');
      setAppliedModeFilter('All');
      setAppliedBatchFilter({ All: ['All'], Online: ['All'], Offline: ['All'] });
      
      // Re-fetch to get updated isPresent values and clear manual selection
      fetchStudentListData(occurrenceId);
      setHasModified(false);
    });
  };

  // Wrapper: toggle student and mark as modified
  const handleToggleStudent = (studentId) => {
    setSelectedStudents(studentId);
    setHasModified(true);
  };

  const toggleTempBatch = (batch) => {
    const currentModeBatches = tempBatchFilter[tempModeFilter];
    let newBatches;
    if (batch === 'All') {
      if (currentModeBatches.includes('All') || currentModeBatches.length === batches.length) {
        newBatches = [];
      } else {
        newBatches = ['All'];
      }
    } else {
      let expandedBatches = currentModeBatches.includes('All') ? [...batches] : currentModeBatches;
      newBatches = expandedBatches.filter(b => b !== 'All');
      if (newBatches.includes(batch)) {
        newBatches = newBatches.filter(b => b !== batch);
      } else {
        newBatches.push(batch);
      }
      
      if (newBatches.length > 0 && newBatches.length === batches.length) {
        newBatches = ['All'];
      }
    }
    setTempBatchFilter({ ...tempBatchFilter, [tempModeFilter]: newBatches });
  };

  const isResetDisabled = tempModeFilter === 'All' && 
    tempBatchFilter['All'].length === 1 && tempBatchFilter['All'][0] === 'All' &&
    tempBatchFilter['Online'].length === 1 && tempBatchFilter['Online'][0] === 'All' &&
    tempBatchFilter['Offline'].length === 1 && tempBatchFilter['Offline'][0] === 'All';

  if (studentListLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="attendance-container">
      {/* Search and Filters */}
      <div className="search-section">
        <div 
          className="search-bar"
          onMouseEnter={() => setIsSearchHovered(true)}
          onMouseLeave={() => setIsSearchHovered(false)}
        >
          <img src={MagnifierIcon} alt="search" className="search-icon-svg" />
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          
          <div 
            className={`search-control-container ${showFilters ? 'active' : ''}`}
            onClick={(e) => {
              if (searchQuery && isSearchHovered) {
                e.stopPropagation();
                setSearchQuery('');
              } else {
                setShowFilters(!showFilters);
              }
            }}
          >
            {searchQuery && isSearchHovered ? (
              <CloseOutlined style={{ fontSize: '18px', color: '#666' }} />
            ) : (
              <img src={FilterIcon} alt="filter" className="filter-icon-svg" />
            )}
          </div>

          {showFilters && (
            <div className="filter-popout" ref={filterRef}>
              <div className="filter-group">
                <h3>Mode</h3>
                <div className="mode-options">
                  {['All', 'Online', 'Offline'].map(mode => (
                    <button
                      key={mode}
                      className={`mode-pill ${tempModeFilter === mode ? 'active' : ''}`}
                      onClick={() => setTempModeFilter(mode)}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>

              <div className="filter-group">
                <h3>Batch</h3>
                <div className="batch-options">
                  <div className="batch-item" onClick={() => toggleTempBatch('All')}>
                    <Checkbox
                      className="rounded-checkbox"
                      checked={tempBatchFilter[tempModeFilter].includes('All')}
                      indeterminate={!tempBatchFilter[tempModeFilter].includes('All') && tempBatchFilter[tempModeFilter].length > 0}
                      style={{ flexShrink: 0, marginRight: '10px' }}
                    />
                    <span>All</span>
                  </div>
                  {batches.map(batch => {
                    const currentModeBatches = tempBatchFilter[tempModeFilter];
                    const isChecked = currentModeBatches.includes(batch) || currentModeBatches.includes('All');
                    return (
                    <div key={batch} className="batch-item" onClick={() => toggleTempBatch(batch)}>
                      <Checkbox
                        className="rounded-checkbox"
                        checked={isChecked}
                        style={{ flexShrink: 0, marginRight: '10px' }}
                      />
                      <Tooltip title={batch} placement="topLeft" mouseEnterDelay={0.4}>
                        <span style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'block',
                          width: '100%'
                        }}>
                          {batch}
                        </span>
                      </Tooltip>
                    </div>
                  )})}
                </div>
              </div>

              <div className="filter-actions">
                <button 
                  className={`btn-reset ${isResetDisabled ? 'disabled' : ''}`} 
                  disabled={isResetDisabled}
                  onClick={() => { 
                    setAppliedModeFilter('All'); 
                    setAppliedBatchFilter({ All: ['All'], Online: ['All'], Offline: ['All'] });
                    setTempModeFilter('All');
                    setTempBatchFilter({ All: ['All'], Online: ['All'], Offline: ['All'] });
                    setShowFilters(false);
                  }}
                >
                  RESET
                </button>
                <button className="btn-apply" onClick={() => {
                  setAppliedModeFilter(tempModeFilter);
                  setAppliedBatchFilter(tempBatchFilter);
                  setShowFilters(false);
                }}>APPLY</button>
              </div>
            </div>
          )}
        </div>

        <div className="filter-summary">
          Mode: <span>{appliedModeFilter}</span> | Batch: <span>{appliedBatchFilter[appliedModeFilter].join(', ')}</span>
        </div>
      </div>

      <div className={`list-scroll-area ${filteredStudents.length === 0 ? 'no-scroll' : ''}`}>
        {filteredStudents.length > 0 && (
          !isBatchMarked ? (
            <div className="section-container">
              <h2 className="section-header">All Students ({String(filteredStudents.length).padStart(2, '0')})</h2>
              {filteredStudents.map((student, idx) => (
                  <StudentCard
                    key={student.studentId}
                    student={student}
                    schedule={schedule}
                    number={idx + 1}
                    isSelected={selectedStudents[student.studentId] === true}
                    onToggle={handleToggleStudent}
                    isScheduledClass={isScheduledClass}
                    canEditAttendance={canEditAttendance}
                    initialPresent={serverPresence(student)}
                  />
              ))}
            </div>
          ) : (
            <>
              {/* Present Section */}
              {presentStudents.length > 0 && (
                <div className="section-container">
                  <h2 className="section-header">Present ({String(presentStudents.length).padStart(2, '0')})</h2>
                  {presentStudents.map((student, idx) => (
                    <StudentCard
                      key={student.studentId}
                      student={student}
                      schedule={schedule}
                      number={idx + 1}
                      isSelected={selectedStudents[student.studentId] === true}
                      onToggle={handleToggleStudent}
                      isScheduledClass={isScheduledClass}
                      canEditAttendance={canEditAttendance}
                      initialPresent={serverPresence(student)}
                    />
                  ))}
                </div>
              )}

              {/* Absent Section */}
              {absentStudents.length > 0 && (
                <div className="section-container">
                  <h2 className="section-header">Absent ({String(absentStudents.length).padStart(2, '0')})</h2>
                  {absentStudents.map((student, idx) => (
                    <StudentCard
                      key={student.studentId}
                      student={student}
                      schedule={schedule}
                      number={idx + 1}
                      isSelected={selectedStudents[student.studentId] === true}
                      onToggle={handleToggleStudent}
                      isScheduledClass={isScheduledClass}
                      canEditAttendance={canEditAttendance}
                      initialPresent={serverPresence(student)}
                    />
                  ))}
                </div>
              )}
            </>
          )
        )}

        {filteredStudents.length === 0 && (
          <div className="empty-state">
            <img src={emptyGhost} alt="no-data" className="empty-ghost-img" />
            <p className="empty-text">No Students found for this class.</p>
          </div>
        )}
      </div>

      {!(isCancelledClass || isScheduledClass) && (
        <div className="footer-action">
          <button
            className={`btn-update ${filteredStudents.length === 0 || updating || !canEditAttendance ? 'disabled' : ''}`}
            onClick={filteredStudents.length > 0 && !updating && canEditAttendance ? handleUpdate : undefined}
            disabled={filteredStudents.length === 0 || updating || !canEditAttendance}
          >
            {updating ? 'UPDATING...' : 'UPDATE'}
          </button>
        </div>
      )}

      <style jsx>{`
        .attendance-container {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: #fff;
          font-family: 'Roboto', sans-serif;
          position: relative;
        }

        .search-section {
          padding: 24px 24px 8px;
        }

        .search-bar {
          display: flex;
          align-items: center;
          height: 48px;
          border: 1.5px solid #E6E6E6;
          border-radius: 12px;
          padding: 0 16px;
          position: relative;
        }

        .search-icon-svg {
          width: 18px;
          height: 18px;
          margin-right: 12px;
          opacity: 0.5;
        }

        .search-bar input {
          flex: 1;
          border: none;
          outline: none;
          font-family: 'Roboto', sans-serif;
          font-weight: 400;
          font-size: 16px;
          line-height: 100%;
          color: #AEAEAE;
          background: transparent;
          letter-spacing: 0%;
        }

        .search-bar input::placeholder {
          color: #AEAEAE;
          opacity: 1;
          font-family: 'Roboto', sans-serif;
          font-weight: 400;
          font-size: 16px;
          line-height: 100%;
        }

        .filter-trigger-container {
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          transition: all 0.2s;
          margin-left: 8px;
        }

        .filter-trigger-container.active {
          background-color: #E6E6E6;
        }

        .search-control-container {
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          transition: all 0.2s;
          margin-left: 8px;
        }

        .search-control-container:hover,
        .search-control-container.active {
          background-color: #E6E6E6;
        }

        .filter-icon-svg {
          width: 20px;
          height: 20px;
        }

        .filter-popout {
          position: absolute;
          top: 56px;
          left: 0;
          right: 0;
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.15);
          z-index: 1000;
          padding: 24px;
        }

        .filter-group {
          margin-bottom: 24px;
        }

        .filter-group h3 {
          font-size: 16px;
          font-weight: 500;
          color: #333;
          margin-bottom: 12px;
        }

        .mode-options {
          display: flex;
          gap: 8px;
        }

        .mode-pill {
          flex: 1;
          height: 36px;
          border-radius: 10px;
          border: 1px solid #E6E6E6;
          background: #fff;
          font-size: 16px;
          color: #666;
          cursor: pointer;
          transition: all 0.2s;
        }

        .mode-pill:focus,
        .mode-pill:focus-visible,
        .mode-pill:active {
          outline: none;
          box-shadow: none;
        }

        .mode-pill.active {
          background: #E8E2FF;
          border-color: #E8E2FF;
          color: #000;
          font-weight: 500;
        }

        .batch-options {
          max-height: 200px;
          overflow-y: auto;
        }

        .batch-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 0;
          cursor: pointer;
        }

        .custom-checkbox {
          width: 20px;
          height: 20px;
          border: 1.5px solid #594099;
          border-radius: 5px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          background: #fff;
        }

        .custom-checkbox.active {
          background: #594099;
        }

        .rounded-checkbox .ant-checkbox-inner {
          border-radius: 4px !important;
        }

        .batch-item span {
          font-size: 16px;
          color: #333;
        }

        .filter-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 16px;
        }

        .btn-reset {
          background: none;
          border: none;
          color: #666;
          font-weight: 600;
          font-size: 16px;
          cursor: pointer;
        }

        .btn-reset.disabled {
          color: #999;
          cursor: not-allowed;
          opacity: 0.6;
        }

        .btn-apply {
          background: none;
          border: none;
          color: #594099;
          font-weight: 600;
          font-size: 16px;
          cursor: pointer;
        }

        .filter-summary {
          margin: 4px 28px 0;
          font-size: 16px;
          color: #999;
          text-align: right;
        }

        .filter-summary span {
          color: #666;
          font-weight: 500;
        }

        .list-scroll-area {
          flex: 1;
          overflow-y: auto;
          padding: 0 24px 100px;
        }

        .list-scroll-area.no-scroll {
          overflow: hidden;
          padding: 0 24px;
        }

        .section-header {
          font-size: 18px;
          font-weight: 500;
          color: #333;
          margin: 16px 0 8px;
        }

        .student-card {
          display: flex;
          align-items: center;
          padding: 10px 0;
          transition: all 0.2s;
        }

        .number {
          min-width: 20px;
          margin-right: 40px;
          font-size: 18px;
          font-weight: 400;
          color: #636363;
        }

        .avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          margin-right: 20px;
          object-fit: cover;
          flex-shrink: 0;
        }

        .avatar-initials {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          margin-right: 20px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 16px;
          text-transform: uppercase;
          line-height: 1;
          padding-top: 2px;
          font-size: 17px;
        }

        /* Takes whatever the row has left over after the duration and the status
           control, so a name runs on until it genuinely reaches them rather than
           stopping at a fixed width with empty space to its right. */
        .student-info {
          flex: 1;
          min-width: 0;
          margin-right: 16px;
        }

        /* Scoped under .student-info to outrank antd's .ant-typography, which
           would otherwise win the colour on these Typography.Text lines. */
        .student-info .student-name {
          font-size: 18px;
          font-weight: 400;
          color: #636363;
          margin-bottom: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 180px;
        }

        .student-info .student-details {
          font-size: 14px;
          font-weight: 400;
          color: #919191;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .attendance-meta {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          margin-right: 40px;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .duration {
          font-size: 18px;
          font-weight: 400;
          color: #636363;
          text-align: right;
        }

        .status-cell {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .status-badge {
          width: 22px;
          height: 22px;
          box-sizing: border-box;
          border-radius: 50%;
          border: 1px solid #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: background 0.2s;
        }

        .status-badge-present {
          background: #00A566;
        }

        .status-badge-absent {
          background: #FF414D;
        }

        /* Manual marking, for students the class has no attendance record for. */
        .check-box-outline {
          width: 20px;
          height: 20px;
          box-sizing: border-box;
          border: 1.5px solid #594099;
          border-radius: 5px;
          background: #fff;
        }

        .check-box-filled {
          width: 20px;
          height: 20px;
          box-sizing: border-box;
          background: #594099;
          border-radius: 5px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .footer-action {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 24px;
          background: rgba(255,255,255,0.9);
          backdrop-filter: blur(4px);
          display: flex;
          justify-content: center;
          border-top: 1px solid #F0F0F0;
        }

        .btn-update {
          width: 220px;
          height: 48px;
          background: #594099;
          color: #fff;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: transform 0.2s;
        }

        .btn-update:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(89,64,153,0.3);
        }

        .empty-state {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          min-height: 400px;
        }

        .empty-ghost-img {
          width: 240px;
          height: auto;
          margin-bottom: 24px;
        }

        .empty-text {
          font-size: 14px;
          color: #636363;
          margin: 0;
        }

        .btn-update.disabled {
          background: #E6E6E6;
          color: #999;
          cursor: not-allowed;
        }

        .btn-update.disabled:hover {
          transform: none;
          box-shadow: none;
        }
      `}</style>
    </div>
  );
}

// Truncation and its tooltip both come from antd, the same way the attendance
// cards do it (scheduleAttendance/components). Typography measures the clipping
// itself, so the tooltip appears only on text that is actually cut off and a
// name that fits gets no tooltip repeating what is already fully on screen.
const EllipsisText = ({ text, className, placement = 'topLeft' }) => (
  <Typography.Text
    className={className}
    style={{ display: 'block', width: '100%' }}
    ellipsis={{
      tooltip: {
        title: text,
        // Each line opens away from the other one. The roll/batch line in
        // particular must not open upwards: it would cover the name sitting
        // directly above it, and a tooltip of roll numbers parked over a name
        // reads as the name's own tooltip gone wrong.
        placement,
        mouseEnterDelay: 0.4,
        // The list sits inside the schedule drawer (z-index 99999), so the
        // overlay has to outrank it or it opens behind the drawer and reads
        // as no tooltip at all.
        zIndex: 100001,
        overlayStyle: { zIndex: 100001 },
        getPopupContainer: () => document.body,
      },
    }}
  >
    {text}
  </Typography.Text>
);

function StudentCard({ student, schedule, number, isSelected, onToggle, isScheduledClass, canEditAttendance, initialPresent }) {
  const durationNum = getDurationMins(student);
  const isAutoMarked = getAttendanceSource(student, schedule) === 'auto';
  // Do not allow manual override for students who were automatically marked (red/green circles)
  const isToggleable = canEditAttendance && !isAutoMarked;
  const isManuallyToggled = isAutoMarked && (isSelected !== initialPresent);

  const displayBatch = getBatchName(student, schedule);

  const shouldShowDuration = isAutoMarked && durationNum > 0;

  return (
    <div 
      className="student-card" 
      onClick={() => isToggleable && onToggle(student.studentId)}
      style={{ cursor: isToggleable ? 'pointer' : 'default' }}
    >
      <div className="number">{String(number).padStart(2, '0')}</div>
      {student.image && !isDefaultImage(student.image) ? (
        <img
          src={`${Api._s3_url}${student.image}`}
          alt={student.name}
          className="avatar"
          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
        />
      ) : null}
      {(isDefaultImage(student.image)) && (
        <div 
          className="avatar-initials" 
          style={{ 
            backgroundColor: getAvatarBg(student.name), 
            color: getAvatarColor(getAvatarBg(student.name)),
            display: 'flex'
          }}
        >
          {getInitials(student.name)}
        </div>
      )}
      <div className="student-info">
        <EllipsisText className="student-name" text={student.name} />
        <EllipsisText className="student-details" placement="bottomLeft" text={`${student.rollNo || student.studentId} | ${displayBatch}`} />
      </div>

      {shouldShowDuration && (
        <div className="attendance-meta">
          <div className="duration">{durationNum} mins</div>
        </div>
      )}

      {/* Shape says how the status was arrived at, fill says what it currently is:
          a circle for a student the class timed against the requirement, a checkbox
          for one who never joined and has to be marked by hand. The circle is fixed -
          it reports what the broadcast measured - while the checkbox follows the live
          selection, so a teacher's marking shows up immediately.

          The checkbox is a control, so it only appears where attendance can actually
          be marked - a cancelled or not-yet-held class has nothing to record and no
          way to record it. The cell itself stays, to keep the column aligned. */}
      <div className="status-cell">
        {isAutoMarked && !isManuallyToggled ? (
          isSelected ? <PresentBadge /> : <AbsentBadge />
        ) : !canEditAttendance ? null : isSelected ? (
          <div className="check-box-filled"><WhiteCheck /></div>
        ) : (
          <div className="check-box-outline" />
        )}
      </div>
    </div>
  );
}

const mapStateToProps = state => ({
  studentAttendanceData: state.schedule.studentAttendanceData,
  selectedStudents: state.schedule.selectedStudents,
  studentListLoading: state.schedule.studentListLoading,
  initialSummary: state.schedule.initialSummary,
  attendanceThresholdMins: state.schedule.attendanceThresholdMins,
  minAttendancePercentageStudent: state.attendanceNew?.getattendanceRequirement?.minAttendancePercentageStudent,
  user: state.session.user
});

const mapDispatchToProps = (dispatch) => ({
  fetchStudentListData: (id) => dispatch(fetchStudentListData(id)),
  attendanceRequirementDetail: () => dispatch(attendanceRequirementDetail()),
  setSelectedStudents: (id) => dispatch(setSelectedStudents(id)),
  postLogOfflineAttendance: (cb) => dispatch(postLogOfflineAttendance(cb)),
  setSelectedId: (id) => dispatch(setSelectedId(id))
});

export default connect(mapStateToProps, mapDispatchToProps)(StudentListComponent);
